import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Save, LogOut, AlertCircle, CheckCircle, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [equipmentMedia, setEquipmentMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsLoggedIn(true);
      loadContent();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', response.data.token);
      setIsLoggedIn(true);
      await loadContent();
    } catch (error) {
      setLoginError('Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };


  const loadEquipmentMedia = async () => {
    try {
      const response = await axios.get(`${API}/equipment-media`);
      setEquipmentMedia(response.data);
    } catch (error) {
      console.error('Failed to load equipment media:', error);
    }
  };

  const handleMediaUpload = async (files) => {
    if (files.length === 0) return;
    
    setUploadingMedia(true);
    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(
        `${API}/admin/equipment-media/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Wait for media to load before showing success
      await loadEquipmentMedia();
      
      // Show success message
      if (response.data && response.data.files) {
        alert(`Successfully uploaded ${response.data.files.length} file(s)!`);
      } else {
        alert('Media uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload media:', error);
      alert('Failed to upload media. Please try again.');
    } finally {
      setUploadingMedia(false);
      // Clear the file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!confirm('Are you sure you want to delete this media?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/equipment-media/${mediaId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      await loadEquipmentMedia();
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete media. Please try again.');
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedIndex(index);
  };

  const handleDragEnd = (e) => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the element
    if (e.currentTarget === e.target) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    console.log('Drag from:', dragIndex, 'to:', dropIndex);
    
    if (dragIndex === dropIndex || isNaN(dragIndex)) return;
    
    // Reorder array
    const newMedia = [...equipmentMedia];
    const draggedItem = newMedia[dragIndex];
    newMedia.splice(dragIndex, 1);
    newMedia.splice(dropIndex, 0, draggedItem);
    
    // Update local state immediately for responsiveness
    setEquipmentMedia(newMedia);
    
    // Send new order to backend
    const newOrder = newMedia.map(item => item.id);
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.put(
        `${API}/admin/equipment-media/reorder`,
        newOrder,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Media reordered successfully:', response.data);
    } catch (error) {
      console.error('Failed to reorder media:', error);
      // Reload to get correct order if failed
      await loadEquipmentMedia();
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadEquipmentMedia();
    }
  }, [isLoggedIn]);


  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setContent(null);
    setPassword('');
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    const token = localStorage.getItem('admin_token');

    try {
      await axios.put(
        `${API}/content`,
        content,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('error');
      console.error('Failed to save content:', error);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const updateService = (index, field, value) => {
    const newServices = [...content.services];
    newServices[index] = { ...newServices[index], [field]: value };
    setContent({ ...content, services: newServices });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-gray-800 border border-green-500/30 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <Lock className="text-green-400 mx-auto mb-4" size={64} />
              <h1 className="text-3xl font-black text-gray-100 mb-2">Admin Panel</h1>
              <p className="text-gray-400">De Rose Transport</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  placeholder="Enter admin password"
                  required
                />
              </div>

              {loginError && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 flex items-center">
                  <AlertCircle size={20} className="mr-2" />
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-4 pb-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header - Sticky */}
        <div className="sticky top-4 z-50 bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-8 flex justify-between items-center shadow-xl" style={{backdropFilter: 'blur(10px)', backgroundColor: 'rgba(31, 41, 55, 0.95)'}}>
          <div>
            <h1 className="text-3xl font-black text-gray-100 mb-1">Admin Panel</h1>
            <p className="text-gray-400">Edit website content</p>
          </div>
          <div className="flex gap-4 flex-wrap">
            <a
              href="/admin/prize-draws"
              className="text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
              style={{backgroundColor: '#01F9C6'}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00E5B4'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#01F9C6'}
            >
              <Award size={20} />
              Prize Draws
            </a>
            <a
              href="/admin/quote-requests"
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              Quote Requests
            </a>
            <a
              href="/admin/career-applications"
              className="bg-purple-500 hover:bg-purple-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              Career Applications
            </a>
            <button
              onClick={handleSave}
              disabled={saveStatus === 'saving'}
              className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <Save size={20} />
              {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleLogout}
              className="bg-gray-700 hover:bg-gray-600 text-gray-100 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>

        {/* Save Status */}
        {saveStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 flex items-center">
            <CheckCircle size={20} className="mr-2" />
            Changes saved successfully! Refresh the main website to see updates.
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 flex items-center">
            <AlertCircle size={20} className="mr-2" />
            Failed to save changes. Please try again.
          </div>
        )}

        {/* Hero Section */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">Hero Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Main Title</label>
              <input
                type="text"
                value={content.hero_title}
                onChange={(e) => updateField('hero_title', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Subtitle</label>
              <input
                type="text"
                value={content.hero_subtitle}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Description (use Enter for new lines)</label>
              <textarea
                value={content.hero_description}
                onChange={(e) => updateField('hero_description', e.target.value)}
                rows="3"
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Location</label>
              <input
                type="text"
                value={content.hero_location}
                onChange={(e) => updateField('hero_location', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">Services</h2>
          
          {content.services.map((service, index) => (
            <div key={index} className="mb-6 p-4 bg-gray-900/50 rounded-lg">
              <h3 className="text-lg font-bold text-yellow-400 mb-4">Service {index + 1}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Title</label>
                  <input
                    type="text"
                    value={service.title}
                    onChange={(e) => updateService(index, 'title', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Description</label>
                  <textarea
                    value={service.description}
                    onChange={(e) => updateService(index, 'description', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* About Section */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">About Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Title</label>
              <input
                type="text"
                value={content.about_title}
                onChange={(e) => updateField('about_title', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Content</label>
              <textarea
                value={content.about_content}
                onChange={(e) => updateField('about_content', e.target.value)}
                rows="5"
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">Contact Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Phone 1</label>
              <input
                type="text"
                value={content.contact_phone1}
                onChange={(e) => updateField('contact_phone1', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Phone 2</label>
              <input
                type="text"
                value={content.contact_phone2}
                onChange={(e) => updateField('contact_phone2', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Email</label>
              <input
                type="email"
                value={content.contact_email}
                onChange={(e) => updateField('contact_email', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 font-semibold mb-2">Address</label>
              <input
                type="text"
                value={content.contact_address}
                onChange={(e) => updateField('contact_address', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>
          </div>
        </div>

        {/* Equipment Section */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">Equipment Lists</h2>
          
          {/* Transport Equipment */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Transport Equipment</h3>
            
            <div className="mb-4">
              <label className="block text-gray-300 font-semibold mb-2">Title</label>
              <input
                type="text"
                value={content.transport_equipment_title || ''}
                onChange={(e) => updateField('transport_equipment_title', e.target.value)}
                placeholder="Transport Equipment"
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>
            
            <p className="text-gray-400 text-sm mb-3">Equipment Items (one per line)</p>
            <textarea
              value={content.transport_equipment?.join('\n') || ''}
              onChange={(e) => updateField('transport_equipment', e.target.value.split('\n').filter(item => item.trim()))}
              rows="15"
              placeholder="BED TRUCK: 390&quot; Planetary Twin Steer w/ Poles&#10;PICKERS: Up to 45 Ton&#10;..."
              className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 font-mono text-sm"
            />
          </div>

          {/* Rental Equipment */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Rental Equipment</h3>
            
            <div className="mb-4">
              <label className="block text-gray-300 font-semibold mb-2">Title</label>
              <input
                type="text"
                value={content.rental_equipment_title || ''}
                onChange={(e) => updateField('rental_equipment_title', e.target.value)}
                placeholder="Rental Equipment"
                className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
              />
            </div>
            
            <p className="text-gray-400 text-sm mb-3">Equipment Items (one per line)</p>
            <textarea
              value={content.rental_equipment?.join('\n') || ''}
              onChange={(e) => updateField('rental_equipment', e.target.value.split('\n').filter(item => item.trim()))}
              rows="20"
              placeholder="Loaders w/Attachments & Grapples&#10;Mobile Boilers&#10;..."
              className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 font-mono text-sm"
            />
          </div>

          {/* Equipment Photos & Videos */}
          <div className="col-span-2">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Equipment Carousel Media (Photos & Videos)</h3>
            <p className="text-gray-400 text-sm mb-3">Upload photos and videos to display in the equipment carousel</p>
            <div className="bg-gray-900 border border-green-500/30 rounded-lg p-6">
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => handleMediaUpload(e.target.files)}
                disabled={uploadingMedia}
                className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-500 file:text-gray-900 hover:file:bg-green-400 cursor-pointer disabled:opacity-50"
              />
              {uploadingMedia && (
                <p className="mt-2 text-green-400 text-sm">Uploading...</p>
              )}
              <div className="mt-2 text-gray-500 text-xs space-y-1">
                <p><strong className="text-green-400">Images:</strong> JPG, PNG, WebP (Recommended: 800x400px)</p>
                <p><strong className="text-green-400">Videos:</strong> MP4, WebM, MOV (Max: 50MB, Auto-play)</p>
                <p><strong className="text-yellow-400">Tip:</strong> Select multiple files at once. Maximum 25 items.</p>
              </div>
              <div className="mt-4 text-sm text-gray-400">
                <p className="font-semibold text-yellow-400 mb-2">Current Media ({equipmentMedia.length}):</p>
                {equipmentMedia.length === 0 ? (
                  <div className="h-24 bg-gray-800 rounded border border-green-500/20 flex items-center justify-center text-gray-600">
                    No media uploaded yet
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {equipmentMedia.map((media, index) => (
                      <div 
                        key={media.id} 
                        className={`relative group cursor-move transition-all ${
                          draggedIndex === index ? 'opacity-30 scale-95' : 'opacity-100 scale-100'
                        } ${
                          dragOverIndex === index && draggedIndex !== index 
                            ? 'ring-4 ring-green-400 scale-105' 
                            : ''
                        }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        <div className="relative border-2 border-green-500/30 rounded hover:border-green-500 transition-all bg-gray-800">
                          {media.type === 'image' ? (
                            <img 
                              src={media.url}
                              alt="Equipment"
                              className="w-full h-24 object-cover rounded"
                            />
                          ) : (
                            <div className="relative">
                              <video 
                                src={media.url}
                                className="w-full h-24 object-cover rounded"
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
                                <div className="bg-green-500 rounded-full p-2">
                                  <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Order number badge */}
                          <div className="absolute top-1 left-1 bg-green-500 text-gray-900 text-xs font-bold px-2 py-1 rounded">
                            #{index + 1}
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteMedia(media.id)}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-400 text-white px-2 py-1 rounded text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Delete
                          </button>
                          
                          {/* Drag handle indicator */}
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-gray-800/90 text-green-400 text-xs px-2 py-1 rounded">
                              ⇅ Drag to reorder
                            </div>
                          </div>
                          
                          {/* Drop zone indicator */}
                          {dragOverIndex === index && draggedIndex !== index && (
                            <div className="absolute inset-0 bg-green-400/20 backdrop-blur-sm rounded flex items-center justify-center">
                              <div className="bg-green-500 text-gray-900 font-bold px-4 py-2 rounded-lg text-sm">
                                Drop here to move to position #{index + 1}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Safety & Compliance */}
          <div>
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Safety & Compliance</h3>
            <p className="text-gray-400 text-sm mb-3">Items with checkmarks (one per line)</p>
            <textarea
              value={content.safety_compliance_items?.join('\n') || ''}
              onChange={(e) => updateField('safety_compliance_items', e.target.value.split('\n').filter(item => item.trim()))}
              rows="8"
              placeholder="Radios, Tire Chains, Gas Monitors&#10;Safety Training (H2S, First Aid, TDG)&#10;..."
              className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 font-mono text-sm"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-green-400 mb-6">Footer</h2>
          
          <div>
            <label className="block text-gray-300 font-semibold mb-2">Tagline</label>
            <input
              type="text"
              value={content.footer_tagline}
              onChange={(e) => updateField('footer_tagline', e.target.value)}
              className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
            />
          </div>
        </div>

        {/* Save Button (bottom) */}
        <div className="flex justify-center">
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className="bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg hover:shadow-green-500/50"
          >
            <Save size={24} />
            {saveStatus === 'saving' ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Admin;
